import { useMutation, useLazyQuery, useApolloClient } from "@apollo/client";

import {
  CHECK_IF_HARD_STOP_EXIST,
  COUNT_WORKER_AT_LINE_JOB,
  GET_TIMELOG_TO_STOP_BY_WORKER,
  INSERT_LINE_JOB_WORKER,
  SET_HARD_START,
  SET_HARD_STOP,
  SET_SOFT_START,
  SET_SOFT_START_WITHOUT_UPDATE,
} from "../../graphql/checkin.graphql";
import { GET_LINE_JOB_BY_ID } from "../../graphql/jobs.graphql";
import { GET_SPECIAL_RATE_BY_PARAMS } from "../../graphql/special-rates.graphql";

import {
  dateToString,
  getLocaleString,
  isPastJob,
  isToday,
  updateDateOnly,
} from "../../utils/date-utils";
import { dataForCheckin } from "../../utils/rate-utils";
import { logTimelogCreate, logTimelogUpdate } from "../../src/utils/timelogAudit";
import { useAuth } from "../../src/context/auth";

import { toast } from "react-toastify";
import { useTranslation } from "next-i18next";

const useCheckLabor = () => {
  const { t } = useTranslation();
  const client = useApolloClient();
  const { userData } = useAuth();

  const [getJobData] = useLazyQuery(GET_LINE_JOB_BY_ID, {
    fetchPolicy: "network-only",
  });

  const [checkIfHardStopExist] = useLazyQuery(CHECK_IF_HARD_STOP_EXIST, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [countWorkerAtLineJob] = useLazyQuery(COUNT_WORKER_AT_LINE_JOB, {
    fetchPolicy: "network-only",
  });

  const [recoverSpecialRate] = useLazyQuery(GET_SPECIAL_RATE_BY_PARAMS, {
    fetchPolicy: "network-only",
  });

  const [loadTimelogToStop] = useLazyQuery(GET_TIMELOG_TO_STOP_BY_WORKER, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [insetLineJobWorker] = useMutation(INSERT_LINE_JOB_WORKER, {
    fetchPolicy: "network-only",
  });

  const [setHardStart] = useMutation(SET_HARD_START, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [setSoftStart] = useMutation(SET_SOFT_START, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [setHardStop] = useMutation(SET_HARD_STOP, {
    fetchPolicy: "network-only", // Used for first execution
  });

  const [setSoftStartWithoutUpdate] = useMutation(
    SET_SOFT_START_WITHOUT_UPDATE,
    {
      fetchPolicy: "network-only", // Used for first execution
    }
  );

  const checkLabor = async (
    afterSubmit,
    worker,
    timelog,
    values,
    setSubmitting,
    checkStatus,
    jobData
  ) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log("🔵 checkLabor CALLED", {
        workerId: worker?.id,
        workerName: `${worker?.first_name} ${worker?.last_name}`,
        checkStatus,
        jobId: jobData?.id,
        timestamp: new Date().toISOString()
      });
    }

    if (!worker || !values) {
      return "";
    }
    setSubmitting(true);

    const jobResponse = await getJobData({
      variables: {
        id: jobData?.id,
      },
    });

    const job = jobResponse?.data?.line_job_by_pk;

    // On the checkin page the user may not be logged in (public kiosk).
    // Fall back to the job's line leader as the audit actor.
    const auditUserId = userData?.id || job?.leader_id;

    const specialRateResponse = await recoverSpecialRate({
      variables: {
        where: {
          deleted_at: { _is_null: true },
          agency_id: { _eq: worker?.agency?.id },
        },
      },
      fetchPolicy: "network-only",
    });

    const specialRates = specialRateResponse?.data?.special_rate || [];

    const { positionId, rate, markup } = dataForCheckin(
      worker,
      job,
      values,
      specialRates
    );

    const isPast = isPastJob(job.date, job.start_time);
    if (process.env.NODE_ENV !== 'production') {
      console.log("🔍 DEBUG isPast:", {
        isPast,
        jobDate: job.date,
        jobStartTime: job.start_time,
        now: new Date()
      });
    }

    // If the job is for the current day use the date otherwise use the job's date
    let laborStartDate = new Date();
    if (isPast) {
      const startTime = new Date(job.start_time);
      const jobDate = new Date(job.date + "T00:00:00");
      const result = new Date(
        jobDate.getFullYear(),
        jobDate.getMonth(),
        jobDate.getDate(),
        startTime.getHours(),
        startTime.getMinutes(),
        0,
        0
      );
      laborStartDate = result;
    }
    
    if (checkStatus === "checkout") {
      try {
        if (process.env.NODE_ENV !== 'production') {
          console.log("🔴 CHECKOUT path - setHardStop", { timelogId: timelog.id, workerId: worker.id });
        }

        const stop = new Date();

        stop.setSeconds(0, 0);

        const updatedStop = updateDateOnly(stop, laborStartDate);

        // Capture before state for audit
        const beforeDataUpdate = { ...timelog, line_job: job, line: job?.line, worker }; // why worker is not included in the timelog?

        const result = await setHardStop({
          variables: {
            reason: values.reason?.value || null,
            description: values.description || null,
            id: timelog.id,
            stop_time: getLocaleString(updatedStop),
            from_job: job.id,
          },
        });

        // Audit log (checked) - UPDATE operation (non-blocking)
        if (auditUserId && result?.data?.update_timelog_by_pk?.id) {
          logTimelogUpdate(client, timelog.id, auditUserId, beforeDataUpdate, result?.data?.update_timelog_by_pk);
        }

        toast.success(t("views.checkin.form.checkoutSuccess"));
      } catch (error) {
        console.log(error);
        setSubmitting(false);
        toast.error(t("general.somethingWrong"));
        return;
      }
    } else {
      try {
        // Return array of timelog that can be stopped
        const timelogResponse = await loadTimelogToStop({
          variables: {
            worker_id: worker.id,
          },
        });

        if (worker.id === job.user?.worker?.id) {
          toast.error(t("views.checkin.form.leaderUnable"));
          setSubmitting(false);
          return;
        }

        // Filter timelogs by job date to isolate past jobs from current jobs
        let timelogToUpdate = null;
        if (timelogResponse?.data?.timelog && timelogResponse.data.timelog.length > 0) {
          const jobDate = dateToString(new Date(job.date + "T00:00:00"));
          const relevantTimelogs = timelogResponse.data.timelog.filter(
            (tl) => dateToString(new Date(tl.date + "T00:00:00")) === jobDate && !tl.stop_time
          );
          if (process.env.NODE_ENV !== 'production') {
            console.log("📋 Timelog filtering", {
              totalTimelogs: timelogResponse.data.timelog.length,
              jobDate,
              relevantTimelogsCount: relevantTimelogs.length,
              relevantTimelogs: relevantTimelogs.map(tl => ({ id: tl.id, date: tl.date, line_job_id: tl.line_job_id }))
            });
          }
          // Only use a timelog if there's exactly one active timelog for this date
          timelogToUpdate = relevantTimelogs.length === 1 ? relevantTimelogs[0] : null;
        }
        if (
          timelogToUpdate === null ||
          isPast ||
          !isToday(timelogToUpdate?.date + "T00:00:00")
        ) {
          const responseCheckHardStop = await checkIfHardStopExist({
            variables: {
              worker_id: worker.id,
              date: dateToString(laborStartDate),
              line_job_id: job.id,
            },
          });
          const count =
            responseCheckHardStop?.data.timelog_aggregate?.aggregate.total || 0;

          const start =
            new Date(job.start_time).getTime() > new Date().getTime()
              ? new Date(job.start_time)
              : new Date();

          start.setSeconds(0, 0);
          const updatedStart = updateDateOnly(start, laborStartDate);

          if (count >= 1) {
            if (process.env.NODE_ENV !== 'production') {
              console.log("🟡 CHECKIN path - setSoftStartWithoutUpdate (count >= 1)", {
                count,
                workerId: worker.id,
                jobId: job.id,
                date: dateToString(laborStartDate)
              });
            }
            const result = await setSoftStartWithoutUpdate({
              variables: {
                worker_id: worker.id,
                position_id: positionId,
                shift: job.shift,
                line_id: job.line_id,
                date: dateToString(laborStartDate),
                start_time: getLocaleString(updatedStart),
                line_job_id: job.id,
                rate,
                markup,
              },
            });

            // Audit log (checked) - INSERT operation (non-blocking)
            if (auditUserId && result?.data?.insert_timelog_one?.id) {
              logTimelogCreate(client, result.data.insert_timelog_one.id, auditUserId, result?.data?.insert_timelog_one);
            }
          } else {
            if (process.env.NODE_ENV !== 'production') {
              console.log("🟢 CHECKIN path - setHardStart (count = 0)", {
                count,
                workerId: worker.id,
                jobId: job.id,
                date: dateToString(laborStartDate)
              });
            }
            const result = await setHardStart({
              variables: {
                worker_id: worker.id,
                position_id: positionId,
                shift: job.shift,
                line_id: job.line_id,
                date: dateToString(laborStartDate),
                start_type: "HARD START",
                start_time: getLocaleString(updatedStart),
                line_job_id: job.id,
                rate,
                markup,
              },
            });

            // Audit log (checked) - INSERT operation (non-blocking)
            if (auditUserId && result?.data?.insert_timelog_one?.id) {
              logTimelogCreate(client, result.data.insert_timelog_one.id, auditUserId, result?.data?.insert_timelog_one);
            }
          }
          toast.success(t("views.checkin.form.checkinSuccess"));
        } else {
          const responseCountWorkerAtLineJob = await countWorkerAtLineJob({
            variables: {
              worker_id: worker.id,
              line_job_id: job.id,
            },
          });
          const countOfWorkerAtLineJob =
            responseCountWorkerAtLineJob?.data?.line_job_worker_aggregate
              ?.aggregate.count || 0;

          if (countOfWorkerAtLineJob == 0) {
            await insetLineJobWorker({
              variables: {
                worker_id: worker.id,
                line_job_id: job.id,
              },
            });
          }

          const start =
            new Date(job.start_time) > new Date()
              ? new Date(job.start_time)
              : new Date();

          start.setSeconds(0, 0);

          const updatedStart = updateDateOnly(start, laborStartDate);

          if (process.env.NODE_ENV !== 'production') {
            console.log("🟠 CHECKIN path - setSoftStart (moving from another job)", {
              timelogToUpdateId: timelogToUpdate.id,
              fromJob: timelogToUpdate.line_job_id,
              toJob: job.id,
              workerId: worker.id,
              date: dateToString(laborStartDate)
            });
          }

          // Capture before state for audit (previous timelog being updated)
          const beforeDataUpdate = { ...timelogToUpdate, line_job: job, line: job?.line };

          const result = await setSoftStart({
            variables: {
              id: timelogToUpdate.id,
              from_job: timelogToUpdate.line_job_id,
              worker_id: worker.id,
              position_id: positionId,
              shift: job.shift,
              line_id: job.line_id,
              date: dateToString(laborStartDate),
              // Stop time of previous timelog and start of the new one
              stop_time: getLocaleString(updatedStart),
              line_job_id: job.id,
              rate,
              markup,
            },
          });

          // Audit log (checked) - UPDATE operation for previous timelog (non-blocking)
          if (auditUserId && result?.data?.update_timelog_by_pk?.id) {
            logTimelogUpdate(client, timelogToUpdate.id, auditUserId, beforeDataUpdate, result?.data?.update_timelog_by_pk);
          }

          // Audit log (checked) - INSERT operation for new timelog (non-blocking)
          if (auditUserId && result?.data?.insert_timelog_one?.id) {
            logTimelogCreate(client, result.data.insert_timelog_one.id, auditUserId, result?.data?.insert_timelog_one);
          }

          toast.success(t("views.checkin.form.checkinSuccess"));
        }
      } catch (error) {
        console.log(error);
        setSubmitting(false);
        toast.error(t("general.somethingWrong"));
        return;
      }
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log("✅ checkLabor COMPLETED successfully", {
        workerId: worker?.id,
        checkStatus,
        jobId: jobData?.id,
        timestamp: new Date().toISOString()
      });
    }

    afterSubmit();

    setSubmitting(false);
  };

  return checkLabor;
};

export { useCheckLabor };
